import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, markTestAccountBrowser } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data?.is_test_account) markTestAccountBrowser();
      setUser(data);
    } catch (e) {
      // Sprint 12+M5.17-h (22.07.2026) — Cross-Browser-Cookie-Fallback.
      // Wenn der Magic-Link im Email-Client geklickt wurde und der Default-
      // Browser ein anderer ist als der, in dem /warteliste ausgefuellt wurde,
      // hat der aktuelle Browser keine Cookie — auch wenn der Backend-Handler
      // /api/auth/magic sie gesetzt hat (in der falschen Browser-Session).
      // Der Backend haengt den JWT als `?auth=<token>` an die Redirect-URL an.
      // Wir tauschen ihn hier gegen einen Cookie im AKTUELLEN Browser.
      try {
        const params = new URLSearchParams(window.location.search);
        const fallbackToken = params.get("auth");
        if (fallbackToken) {
          // Exchange-Endpoint setzt Cookie + gibt User-JSON zurueck.
          await api.post("/auth/magic/exchange", { token: fallbackToken });
          // URL bereinigen (Token soll nicht in History bleiben).
          params.delete("auth");
          const q = params.toString();
          const cleanUrl = window.location.pathname + (q ? `?${q}` : "") + window.location.hash;
          window.history.replaceState({}, "", cleanUrl);
          // Cookie ist jetzt gesetzt — /auth/me nochmal probieren.
          const { data } = await api.get("/auth/me");
          setUser(data);
          console.info("[AUTH] cross-browser-fallback ok — token exchanged, user hydrated");
          return;
        }
      } catch (fallbackErr) {
        console.warn("[AUTH] cross-browser-fallback failed:", fallbackErr?.response?.status || fallbackErr?.message);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  // Sprint 11.6 Block 2 (Task 5) — Conversion-Boost: optional fokustyp + source
  // aus Query-Params direkt beim Register mit-übergeben (Backend speichert
  // user.primary_fokustyp). User muss FT nicht doppelt angeben.
  const register = async (email, password, name, extras = {}) => {
    try {
      const body = { email, password, name };
      if (extras.fokustyp) body.fokustyp = extras.fokustyp;
      if (extras.source) body.source = extras.source;
      // Sprint V1.7 Referral-Engine System A (09.09.2026) — Sign-Up-Tracking.
      // 1) explizit uebergebener ref_code hat Vorrang, 2) sonst aus localStorage.
      let refCode = extras.ref_code || null;
      if (!refCode) {
        try {
          const raw = localStorage.getItem("f4s_ref_code");
          if (raw) {
            const parsed = JSON.parse(raw);
            const age = Date.now() - (parsed?.ts || 0);
            if (parsed?.code && age < (parsed?.ttl || 0)) {
              refCode = parsed.code;
            } else {
              localStorage.removeItem("f4s_ref_code");
            }
          }
        } catch { /* ignore */ }
      }
      if (refCode) body.ref_code = refCode;
      const { data } = await api.post("/auth/register", body);
      // Clear ref code after successful use (Single-Use semantics).
      if (refCode) {
        try { localStorage.removeItem("f4s_ref_code"); } catch { /* ignore */ }
      }
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore logout errors - we clear state anyway
    }
    setUser(false);
  };

  // Sprint V102.i.2 (Boardroom-Directive) — Session-Loop-Fix.
  // Wird von Datenladern (z.B. DashboardLayout) beim 401 aufgerufen, damit
  // Login.jsx (das useAuth().user als Weiterleitungs-Trigger nutzt) nicht
  // sofort wieder auf /cockpit zurueckredirected — sondern korrekt das
  // Login-Formular anzeigt. Ohne diesen Aufruf bleibt der User-Cache stale
  // und der Redirect-Loop /cockpit -> /login -> /cockpit flackert.
  const clearUser = useCallback(() => {
    setUser(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh: fetchMe, clearUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
